import { CloseOutlined } from '@ant-design/icons';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { useViewActions } from '@mm/state/actions/viewActions';
import { IntegrationTypes } from '@mm/state/model/ConnectionModel';
import { useConnectionsTableActions } from '@mm/state/modules/connectionsTableState/actions';
import { selectCrmsLeadsSent } from '@mm/state/selectors/connectionsSelectors';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { SlideoutMenu } from '../../../../../components/SlideoutMenu/components/SlideoutMenu';
import { useSlideoutOnClose } from '../../../../../components/SlideoutMenu/menus/SlideoutEmail/SlideoutEmail';
import ConnectionsTableGrid from '../../../../connections/components/ConnectionsTableGrid/ConnectionsTableGrid';
import ZapierPanel from '../../../../zapier/panels/ZapierPanel/ZapierPanel';
import { CSVIntegrations } from './components/CSVIntegrations/CSVIntegrations';
import { FacebookAdsIntegrations } from './components/FacebookAdsIntegrations/FacebookAdsIntegrations';
import { GoHighLevelIntegration } from './components/GoHighLevelIntegrations/GoHighLevelIntegrations';
import { GoogleAdsIntegrations } from './components/GoogleAdsIntegrations/GoogleAdsIntegrations';
import { GoogleSheetsIntegration } from './components/GoogleSheetsIntegrations/GoogleSheetsIntegration';
import { HubspotIntegrations } from './components/HubspotIntegrations/HubspotIntegrations';
import { KlaviyoIntegrations } from './components/KlaviyoIntegrations/KlaviyoIntegrations';
import { MailchimpIntegration } from './components/MailchimpIntegrations/MailchimpIntegration';
import { ManualIntegrations } from './components/ManualIntegrations/ManualIntegrations';
import { OmnisendIntegrations } from './components/OmnisendIntegrations/OmnisendIntegrations';
import { SalesForceIntegrations } from './components/SalesForceIntegrations/SalesForceIntegrations';
import { SendlaneIntegrations } from './components/SendlaneIntegrations/SendlaneIntegrations';
import { ShopifyIntegrations } from './components/ShopifyIntegrations/ShopifyIntegrations';
import { SlackIntegrations } from './components/SlackIntegrations/SlackIntegrations';
import styles from './NativeIntegrations.module.css';

export type NativeIntegrationSlideoutEditorProps = {
  type: IntegrationTypes;
};

export const NativeIntegrations = ({ type }: { type: IntegrationTypes }) => {
  const { resetState } = useConnectionsTableActions();
  const { retriveCrmsLeadsSent } = useConnectionCRUDActions();
  const activeFbPageId = useSelector(activeFacebookPageIdSelector);
  const crmsLeadsSent = useSelector(selectCrmsLeadsSent);

  const { setRef } = useSlideoutOnClose();
  const { hideSlideoutMenu } = useViewActions();

  const closeSlideout = async () => {
    hideSlideoutMenu();
  };

  const component = () => {
    switch (type) {
      case 'salesforce':
        return <SalesForceIntegrations />;
      case 'gohighlevel':
        return <GoHighLevelIntegration />;
      case 'shopify':
        return <ShopifyIntegrations />;
      case 'klaviyo':
        return <KlaviyoIntegrations />;
      case 'sendlane':
        return <SendlaneIntegrations />;
      case 'mailchimp':
        return <MailchimpIntegration />;
      case 'zapier':
        return <ZapierPanel />;
      case 'default':
        return <ConnectionsTableGrid />;
      case 'google_spreadsheet':
        return <GoogleSheetsIntegration />;
      case 'hubspot':
        return <HubspotIntegrations />;
      case 'omnisend':
        return <OmnisendIntegrations />;
      case 'slack':
        return <SlackIntegrations />;
      case 'facebook_ads_audience':
        return <FacebookAdsIntegrations />;
      case 'google_ads_audience':
        return <GoogleAdsIntegrations />;
      case 'csv_file':
        return <CSVIntegrations />;
      case 'manual_suppression':
        return <ManualIntegrations />;
      default:
        return null;
    }
  };

  useEffect(() => {
    resetState();
  }, [resetState]);

  useEffect(() => {
    if (!crmsLeadsSent) {
      retriveCrmsLeadsSent(activeFbPageId);
    }
  }, []);

  return (
    <SlideoutMenu className={styles.menu} setRef={setRef}>
      <div className={styles.headerContent}>
        <CloseOutlined onClick={closeSlideout} className={styles.closeIcon} />
      </div>
      <SlideoutMenu.Content className={styles.content}>
        {component()}
      </SlideoutMenu.Content>
    </SlideoutMenu>
  );
};
